/**
 * Ett möte: status i klartext, sammanfattning, beslut, åtgärder, utskrift,
 * ljud, deltagare, typ, kunskapsbasen och radering.
 *
 * Renderas med `key={id}` från sidan, så att ett byte av möte alltid börjar
 * från ett tomt läge (route med :id återanvänder annars komponenten).
 *
 * Vyn hämtar om mötet var 10:e sekund så länge något pågår (var 60:e när
 * ljudet väntar på en utskrift som inte är påslagen), och bara när fliken syns.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Input, Modal, Spinner } from '@/components/ui';
import { motenApi, type MotesDeltagare, type MotesDetalj } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatFranMime, valjInspelningsMime } from '@/hooks/useInspelning';
import { kanLaggaIKb } from '../../../../netlify/functions/_shared/moten/behorighet';
import type { Mote, MotesTyp } from '../../../../netlify/functions/_shared/moten/typer';
import { Besked, Inbjudningsrad, Sektion, StatusPiller } from './MotesDelar';
import { RaderaMote } from './RaderaMote';
import { Utskrift } from './Utskrift';
import {
  KALLA_ETIKETT,
  TYP_ETIKETT,
  TYP_FORKLARING,
  arPagaende,
  datumText,
  datumTidText,
  felAtgarder,
  felText,
  pollMs,
  statusBesked,
} from './motesText';

type Atgard = 'forsok_igen' | 'skicka_in_nu' | 'avsluta' | 'ljud_klar' | 'lagg_i_kb';

export function MotesVy({ id }: { id: string }) {
  const navigate = useNavigate();
  const nyttMote = (useLocation().state as { nytt?: boolean } | null)?.nytt === true;
  const rubrikRef = useRef<HTMLHeadingElement>(null);
  const fokuserad = useRef(false);
  const [detalj, setDetalj] = useState<MotesDetalj | null>(null);
  const [laddFel, setLaddFel] = useState<{
    text: string;
    saknas: boolean;
  } | null>(null);
  const [upptagen, setUpptagen] = useState<Atgard | null>(null);
  const [besked, setBesked] = useState<{
    ton: 'fel' | 'info';
    text: string;
  } | null>(null);
  const [raderaOppen, setRaderaOppen] = useState(false);
  const senaste = useRef(0);

  const [nu, setNu] = useState(0);

  const hamta = useCallback(async () => {
    const nr = ++senaste.current;
    let svar: { d: MotesDetalj } | { fel: unknown };
    try {
      svar = { d: await motenApi<MotesDetalj>({ atgard: 'hamta', id }) };
    } catch (e) {
      svar = { fel: e };
    }
    // Bara det senaste anropet får skriva; ett långsamt äldre svar kastas.
    if (nr !== senaste.current) return;
    setNu(Date.now());
    if ('d' in svar) {
      setDetalj(svar.d);
      setLaddFel(null);
    } else {
      const status = (svar.fel as { status?: number }).status;
      setLaddFel({
        text: felText(svar.fel, 'Att hämta mötet'),
        saknas: status === 404,
      });
    }
  }, [id]);

  useEffect(() => {
    const t = setTimeout(() => void hamta(), 0);
    return () => clearTimeout(t);
  }, [hamta]);

  // Omhämtning medan något pågår – bara när fliken syns.
  const status = detalj?.mote.status;
  const intervall = status ? pollMs(status, detalj?.stt_pa) : null;
  useEffect(() => {
    if (intervall === null) return;
    let t: ReturnType<typeof setInterval> | null = null;
    const starta = () => {
      if (t === null) t = setInterval(() => void hamta(), intervall);
    };
    const stoppa = () => {
      if (t !== null) clearInterval(t);
      t = null;
    };
    const synlighet = () => {
      if (document.visibilityState === 'visible') {
        void hamta();
        starta();
      } else stoppa();
    };
    if (document.visibilityState === 'visible') starta();
    document.addEventListener('visibilitychange', synlighet);
    return () => {
      stoppa();
      document.removeEventListener('visibilitychange', synlighet);
    };
  }, [intervall, hamta]);

  // Ett nyss skapat möte: fokus till rubriken, så att tangentbord och skärmläsare hamnar i mötet.
  const harDetalj = detalj !== null;
  useEffect(() => {
    if (!nyttMote || !harDetalj || fokuserad.current) return;
    fokuserad.current = true;
    rubrikRef.current?.focus();
  }, [nyttMote, harDetalj]);

  async function kor(namn: Atgard, fn: () => Promise<string | void>) {
    setUpptagen(namn);
    setBesked(null);
    try {
      const klart = await fn();
      if (klart) setBesked({ ton: 'info', text: klart });
      await hamta();
    } catch (e) {
      setBesked({ ton: 'fel', text: felText(e) });
    } finally {
      setUpptagen(null);
    }
  }

  if (!detalj) {
    return (
      <div className="max-w-[1100px] mx-auto">
        <Tillbaka />
        {laddFel ? (
          <div className="mt-4 rounded-xl border border-ink-200 bg-surface p-6">
            <p className="text-[14px] font-semibold text-ink-900">
              {laddFel.saknas ? 'Mötet finns inte' : 'Mötet kunde inte hämtas'}
            </p>
            <p className="text-[13px] text-ink-600 mt-1">{laddFel.text}</p>
            {!laddFel.saknas && (
              <Button variant="secondary" size="sm" className="mt-4" onClick={() => void hamta()}>
                Försök igen
              </Button>
            )}
          </div>
        ) : (
          <div className="flex justify-center py-16">
            <Spinner size={24} className="text-brand-500" label="Hämtar mötet" />
          </div>
        )}
      </div>
    );
  }

  const { mote: m, deltagare, kan } = detalj;
  const agare = kan.andra;
  const talarnamn = m.talarnamn ?? {};
  const namn = (t: string | null) => (t ? talarnamn[t]?.trim() || t : null);

  return (
    <div className="max-w-[1100px] mx-auto pb-10 space-y-4">
      <Tillbaka />

      <header className="rounded-xl border border-ink-200 bg-surface shadow-card p-5 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6">
          <div className="min-w-0 flex-1">
            <h2
              ref={rubrikRef}
              tabIndex={-1}
              className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 text-[20px] font-semibold tracking-[-0.015em] text-ink-900 leading-tight break-words"
            >
              {m.titel}
            </h2>
            <p className="text-[12.5px] text-ink-500 mt-1.5">
              {[
                datumText(m.motesdatum ?? m.skapad_at),
                TYP_ETIKETT[m.typ],
                KALLA_ETIKETT[m.kalla],
                agare ? 'Ditt möte' : m.agare_epost ? `Ägare: ${m.agare_epost}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <StatusPiller status={m.status} kalla={m.kalla} fel={m.fel} className="self-start" />
        </div>

        {m.status !== 'sammanfattad' && (
          <StatusRuta
            mote={m}
            nu={nu}
            agare={agare}
            upptagen={upptagen}
            onForsokIgen={() =>
              kor('forsok_igen', async () => {
                await motenApi({ atgard: 'forsok_igen', id: m.id });
              })
            }
            onSkickaInNu={() =>
              kor('skicka_in_nu', async () => {
                const r = await motenApi<{ skickad: boolean; fel?: string }>({
                  atgard: 'skicka_in_nu',
                  id: m.id,
                });
                return r.skickad ? 'Boten är på väg in i mötet. Släpp in den när den knackar på.' : undefined;
              })
            }
            onAvslutaInspelning={() => kor('avsluta', () => avslutaInspelning(m.id))}
            onLjudKlar={() =>
              kor('ljud_klar', async () => {
                await motenApi({ atgard: 'ljud_klar', id: m.id });
              })
            }
            onRadera={kan.radera ? () => setRaderaOppen(true) : null}
          />
        )}
        {besked && (
          <div className="mt-3">
            <Besked ton={besked.ton}>{besked.text}</Besked>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <Resultat mote={m} namn={namn} />
          {m.utskrift && m.utskrift.length > 0 && (
            <Sektion rubrik="Utskrift" id={`utskrift-${m.id}`}>
              <Utskrift
                segment={m.utskrift}
                talarnamn={talarnamn}
                kanAndra={agare}
                onSparaNamn={async (nya) => {
                  try {
                    await motenApi({
                      atgard: 'dopa_talare',
                      id: m.id,
                      talarnamn: nya,
                    });
                  } catch (e) {
                    throw new Error(felText(e, 'Att spara namnen'));
                  }
                  await hamta();
                }}
              />
            </Sektion>
          )}
        </div>

        <aside className="space-y-4 min-w-0">
          {m.typ === 'kund' && <Inbjudningsrad />}
          <Ljud mote={m} />
          <Deltagare
            mote={m}
            deltagare={deltagare}
            agare={agare}
            onAndrad={(d) => setDetalj((x) => (x ? { ...x, deltagare: d } : x))}
          />
          <Typ mote={m} agare={agare} onBytt={() => void hamta()} />
          <Kunskapsbas
            mote={m}
            kan={kan.lagg_i_kb && kanLaggaIKb(m)}
            upptagen={upptagen === 'lagg_i_kb'}
            agare={agare}
            onLaggIn={() =>
              kor('lagg_i_kb', async () => {
                await motenApi({ atgard: 'lagg_i_kb', id: m.id });
                return 'Mötet ligger nu i kunskapsbasen.';
              })
            }
          />
          {kan.radera && (
            <div className="px-1">
              <button
                type="button"
                onClick={() => setRaderaOppen(true)}
                className="text-[13px] font-semibold text-red-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
              >
                Radera mötet
              </button>
            </div>
          )}
        </aside>
      </div>

      <RaderaMote
        mote={raderaOppen ? { id: m.id, titel: m.titel } : null}
        iKunskapsbasen={!!m.kb_tillagd_at}
        onStang={() => setRaderaOppen(false)}
        onRaderat={() => navigate('/moten', { replace: true })}
      />
    </div>
  );
}

/**
 * Inspelning vars flik stängdes: avsluta med det som hann laddas upp. Formatet
 * är okänt här, så webbläsarens eget format provas först och det andra sedan.
 */
async function avslutaInspelning(id: string): Promise<string> {
  const forst = formatFranMime(valjInspelningsMime());
  const ordning = forst === 'webm' ? ['webm', 'mp4'] : ['mp4', 'webm'];
  let sistaFel: unknown = null;
  for (const format of ordning) {
    try {
      await motenApi({ atgard: 'ljud_klar', id, format });
      return 'Inspelningen är avslutad och skrivs ut.';
    } catch (e) {
      sistaFel = e;
      if ((e as { status?: number }).status !== 409) break;
    }
  }
  throw sistaFel;
}

function Tillbaka() {
  return (
    <Link
      to="/moten"
      className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-600 hover:text-ink-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <span aria-hidden>←</span> Alla möten
    </Link>
  );
}

/* ---------- Status ---------- */

function StatusRuta({
  mote: m,
  nu,
  agare,
  upptagen,
  onForsokIgen,
  onSkickaInNu,
  onAvslutaInspelning,
  onLjudKlar,
  onRadera,
}: {
  mote: Mote;
  /** Tidpunkten för senaste hämtningen (ms) – render får inte läsa klockan. */
  nu: number;
  agare: boolean;
  upptagen: Atgard | null;
  onForsokIgen: () => void;
  onSkickaInNu: () => void;
  onAvslutaInspelning: () => void;
  onLjudKlar: () => void;
  /** null = användaren får inte radera. */
  onRadera: (() => void) | null;
}) {
  const b = statusBesked(m, new Date(nu));
  const fel = m.status === 'fel';
  const pagar = arPagaende(m.status);
  const startPasserad = !m.starttid || new Date(m.starttid).getTime() <= nu;
  const atgarder = felAtgarder(m);
  const lasa = upptagen !== null;

  const knappar: ReactElement[] = [];
  if (agare && atgarder.includes('forsok_igen')) {
    knappar.push(
      <Button key="f" size="sm" onClick={onForsokIgen} loading={upptagen === 'forsok_igen'} disabled={lasa}>
        Försök igen
      </Button>,
    );
  }
  if (agare && atgarder.includes('skicka_in_nu')) {
    // skicka_in_nu nollar felet och gör anspråk i samma UPDATE (fel → bot_skickad).
    knappar.push(
      <Button key="s" size="sm" onClick={onSkickaInNu} loading={upptagen === 'skicka_in_nu'} disabled={lasa}>
        Skicka in boten nu
      </Button>,
    );
  }
  if (onRadera && atgarder.includes('radera')) {
    knappar.push(
      <Button key="r" size="sm" variant="secondary" onClick={onRadera} disabled={lasa}>
        Radera mötet
      </Button>,
    );
  }
  const agarenKan = atgarder.includes('radera')
    ? 'Mötets ägare kan radera det.'
    : atgarder.includes('skicka_in_nu')
      ? 'Mötets ägare kan skicka in boten igen.'
      : 'Mötets ägare kan försöka igen.';
  if (agare && m.status === 'bokad') {
    knappar.push(
      <Button
        key="s"
        size="sm"
        variant={startPasserad ? 'primary' : 'secondary'}
        onClick={onSkickaInNu}
        loading={upptagen === 'skicka_in_nu'}
        disabled={lasa}
      >
        Skicka in nu
      </Button>,
    );
  }
  if (agare && m.status === 'vantar_ljud' && m.kalla === 'inspelning') {
    knappar.push(
      <Button key="a" size="sm" onClick={onAvslutaInspelning} loading={upptagen === 'avsluta'} disabled={lasa}>
        Avsluta inspelningen
      </Button>,
    );
  }
  if (agare && m.status === 'vantar_ljud' && m.kalla === 'uppladdning') {
    knappar.push(
      <Button
        key="l"
        size="sm"
        variant="secondary"
        onClick={onLjudKlar}
        loading={upptagen === 'ljud_klar'}
        disabled={lasa}
      >
        Filen är uppladdad – fortsätt
      </Button>,
    );
  }

  return (
    <div
      className={cn(
        'mt-5 rounded-lg border px-4 py-3.5',
        fel ? 'border-red-200 bg-red-50' : 'border-ink-200 bg-surface-sunken',
      )}
      role={fel ? 'alert' : undefined}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className={cn('text-[13.5px] font-semibold', fel ? 'text-red-900' : 'text-ink-900')}>
            {pagar && <span className="sr-only">Pågår: </span>}
            {b.rubrik}
          </p>
          <p className={cn('text-[13px] mt-0.5 leading-relaxed', fel ? 'text-red-800' : 'text-ink-600')}>{b.text}</p>
          {fel && !agare && <p className="text-[12.5px] text-red-800 mt-1">{agarenKan}</p>}
        </div>
        {knappar.length > 0 && <div className="flex flex-wrap gap-2 shrink-0">{knappar}</div>}
      </div>
    </div>
  );
}

/* ---------- Sammanfattning, beslut, åtgärder ---------- */

function Resultat({ mote: m, namn }: { mote: Mote; namn: (t: string | null) => string | null }) {
  if (!m.sammanfattning) {
    return (
      <Sektion rubrik="Sammanfattning">
        <p className="text-[13px] text-ink-500">
          {m.status === 'fel'
            ? 'Ingen sammanfattning – mötet stannade innan den hann skrivas.'
            : 'Sammanfattning, beslut och åtgärder visas här när mötet är utskrivet och sammanfattat.'}
        </p>
      </Sektion>
    );
  }
  const beslut = m.beslut ?? [];
  const atgarder = m.atgarder ?? [];
  return (
    <>
      <Sektion rubrik="Sammanfattning">
        <div className="text-[14px] text-ink-800 leading-relaxed whitespace-pre-line break-words">
          {m.sammanfattning}
        </div>
      </Sektion>
      <Sektion rubrik="Beslut">
        {beslut.length === 0 ? (
          <p className="text-[13px] text-ink-500">Inga beslut fattades enligt utskriften.</p>
        ) : (
          <ul className="space-y-2">
            {beslut.map((b, i) => (
              <li key={i} className="flex gap-2.5 text-[13.5px] text-ink-800 leading-relaxed">
                <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-ink-400 shrink-0" aria-hidden />
                <span className="min-w-0 break-words">{b}</span>
              </li>
            ))}
          </ul>
        )}
      </Sektion>
      <Sektion rubrik="Åtgärder">
        {atgarder.length === 0 ? (
          <p className="text-[13px] text-ink-500">Inga åtgärder togs upp.</p>
        ) : (
          <ul className="divide-y divide-ink-200 -my-1">
            {atgarder.map((a, i) => {
              const vem = namn(a.vem);
              return (
                <li key={i} className="py-2.5">
                  <p className="text-[13.5px] text-ink-900 leading-relaxed break-words">{a.vad}</p>
                  <p className="text-[12.5px] text-ink-500 mt-0.5">
                    {vem ? `Vem: ${vem}` : 'Ingen ansvarig nämndes'}
                    {a.nar ? ` · När: ${a.nar}` : ''}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </Sektion>
    </>
  );
}

/* ---------- Ljud ---------- */

function Ljud({ mote: m }: { mote: Mote }) {
  const [src, setSrc] = useState<string | null>(null);
  const [laddar, setLaddar] = useState(false);
  const [fel, setFel] = useState<string | null>(null);
  const objektUrl = useRef<string | null>(null);
  const ljudRef = useRef<HTMLAudioElement>(null);
  /** När den signerade URL:en slutar gälla (ms). null = lokal blob, går aldrig ut. */
  const gallerTill = useRef<number | null>(null);
  /** Position och uppspelning att återställa när en ny URL har laddats. */
  const aterstall = useRef<{ tid: number; spela: boolean } | null>(null);
  const fornyelser = useRef<number[]>([]);
  const fornyar = useRef(false);

  useEffect(
    () => () => {
      if (objektUrl.current) URL.revokeObjectURL(objektUrl.current);
    },
    [],
  );

  if (!m.ljud_path) {
    if (m.kalla === 'klang' || m.status === 'vantar_ljud') return null;
    return (
      <Sektion rubrik="Ljud">
        <p className="text-[13px] text-ink-500">Ljudet är raderat. Det sparas i 30 dagar efter mötet.</p>
      </Sektion>
    );
  }

  /** En spelbar adress: den signerade URL:en, eller inspelningens delar ihoplagda. */
  async function hamtaKalla(): Promise<string> {
    const r = await motenApi<{ urls: string[]; giltig_sek?: number }>({ atgard: 'ljud_url', id: m.id });
    if (r.urls.length === 1) {
      // Förnyas en halv minut innan den går ut – marginal mot nät och klockskillnad.
      gallerTill.current = Date.now() + Math.max(30, (r.giltig_sek ?? 300) - 30) * 1000;
      return r.urls[0];
    }
    // Inspelningens delar hör till en och samma fil – lagda efter varandra blir de spelbara.
    const delar = await Promise.all(
      r.urls.map(async (u) => {
        const svar = await fetch(u);
        if (!svar.ok) throw new Error('En del av ljudet kunde inte hämtas.');
        return svar.blob();
      }),
    );
    const typ = m.ljud_format === 'mp4' ? 'audio/mp4' : 'audio/webm';
    const url = URL.createObjectURL(new Blob(delar, { type: typ }));
    if (objektUrl.current) URL.revokeObjectURL(objektUrl.current);
    objektUrl.current = url;
    gallerTill.current = null;
    return url;
  }

  async function spela() {
    setLaddar(true);
    setFel(null);
    fornyelser.current = [];
    try {
      aterstall.current = { tid: 0, spela: true };
      setSrc(await hamtaKalla());
    } catch (e) {
      setFel(felText(e, 'Att hämta ljudet'));
    } finally {
      setLaddar(false);
    }
  }

  /**
   * Den signerade URL:en gäller i fem minuter. Går den ut (fel vid spolning
   * eller fortsatt uppspelning) hämtas en ny, och position och uppspelning
   * återställs. Högst tre förnyelser per minut – sedan tillbaka till knappen.
   */
  async function fornya(spela: boolean) {
    const el = ljudRef.current;
    if (fornyar.current || !el) return;
    const nu = Date.now();
    fornyelser.current = fornyelser.current.filter((t) => nu - t < 60_000);
    if (fornyelser.current.length >= 3) {
      setSrc(null);
      setFel('Ljudet kunde inte spelas upp. Tryck Spela upp för att försöka igen.');
      return;
    }
    fornyelser.current.push(nu);
    fornyar.current = true;
    aterstall.current = { tid: el.currentTime || 0, spela };
    try {
      setSrc(await hamtaKalla());
    } catch (e) {
      setSrc(null);
      setFel(`Ljudet kunde inte spelas upp. ${felText(e, 'Att hämta ljudet')}`);
    } finally {
      fornyar.current = false;
    }
  }

  const utgangen = () => gallerTill.current !== null && Date.now() > gallerTill.current;

  return (
    <Sektion rubrik="Ljud">
      {src ? (
        <audio
          ref={ljudRef}
          controls
          src={src}
          className="w-full"
          onLoadedMetadata={(e) => {
            const a = aterstall.current;
            aterstall.current = null;
            if (!a) return;
            if (a.tid > 0) e.currentTarget.currentTime = a.tid;
            if (a.spela) void e.currentTarget.play().catch(() => {});
          }}
          onError={() => {
            if (gallerTill.current === null) {
              setSrc(null);
              setFel('Ljudet kunde inte spelas upp i den här webbläsaren.');
              return;
            }
            const el = ljudRef.current;
            void fornya(!!el && (!el.paused || aterstall.current?.spela === true));
          }}
          onPlay={(e) => {
            if (utgangen()) {
              e.currentTarget.pause();
              void fornya(true);
            }
          }}
          onSeeking={(e) => {
            if (utgangen()) void fornya(!e.currentTarget.paused);
          }}
        >
          Webbläsaren kan inte spela upp ljudet.
        </audio>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => void spela()} loading={laddar}>
          Spela upp
        </Button>
      )}
      {fel && (
        <div className="mt-3">
          <Besked ton="fel">{fel}</Besked>
        </div>
      )}
      {m.ljud_raderas_at && (
        <p className="text-[12px] text-ink-500 mt-3">
          Ljudet raderas {datumText(m.ljud_raderas_at)}. Texten ligger kvar.
        </p>
      )}
    </Sektion>
  );
}

/* ---------- Deltagare ---------- */

function Deltagare({
  mote: m,
  deltagare,
  agare,
  onAndrad,
}: {
  mote: Mote;
  deltagare: MotesDeltagare[];
  agare: boolean;
  onAndrad: (d: MotesDeltagare[]) => void;
}) {
  const uid = useId();
  const [epost, setEpost] = useState('');
  const [upptagen, setUpptagen] = useState<string | null>(null);
  const [fel, setFel] = useState<string | null>(null);

  async function laggTill() {
    const e = epost.trim();
    if (!e) return;
    setUpptagen('ny');
    setFel(null);
    try {
      const r = await motenApi<{ deltagare: MotesDeltagare[] }>({
        atgard: 'deltagare_lagg_till',
        id: m.id,
        epost: e,
      });
      onAndrad(r.deltagare);
      setEpost('');
    } catch (err) {
      setFel(felText(err, 'Att lägga till deltagaren'));
    } finally {
      setUpptagen(null);
    }
  }

  async function taBort(userId: string) {
    setUpptagen(userId);
    setFel(null);
    try {
      const r = await motenApi<{ deltagare: MotesDeltagare[] }>({
        atgard: 'deltagare_ta_bort',
        id: m.id,
        user_id: userId,
      });
      onAndrad(r.deltagare);
    } catch (err) {
      setFel(felText(err, 'Att ta bort deltagaren'));
    } finally {
      setUpptagen(null);
    }
  }

  return (
    <Sektion rubrik="Deltagare">
      <p className="text-[12.5px] text-ink-600 mb-3 leading-relaxed">
        {m.typ === 'intern_alla'
          ? 'Alla på El-kretsen kan läsa mötet. Tillagda deltagare ser det under Delade med mig.'
          : 'Bara ägaren och deltagarna nedan kan läsa mötet.'}
      </p>
      <ul className="space-y-1.5">
        <li className="flex items-center justify-between gap-2 text-[13px]">
          <span className="min-w-0 truncate text-ink-900">{m.agare_epost ?? 'Ägaren'}</span>
          <span className="text-[12px] text-ink-500 shrink-0">ägare</span>
        </li>
        {deltagare.map((d) => (
          <li key={d.user_id} className="flex items-center justify-between gap-2 text-[13px]">
            <span className="min-w-0 truncate text-ink-900">{d.epost ?? 'Okänt konto'}</span>
            {agare && (
              <button
                type="button"
                onClick={() => void taBort(d.user_id)}
                disabled={upptagen !== null}
                className="shrink-0 text-[12px] font-semibold text-ink-600 hover:text-ink-900 disabled:opacity-40 rounded px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                aria-label={`Ta bort ${d.epost ?? 'deltagaren'}`}
              >
                {upptagen === d.user_id ? 'Tar bort…' : 'Ta bort'}
              </button>
            )}
          </li>
        ))}
      </ul>
      {agare && (
        <form
          className="mt-4"
          onSubmit={(e) => {
            e.preventDefault();
            void laggTill();
          }}
        >
          <label htmlFor={`${uid}-epost`} className="block text-[12px] font-semibold text-ink-700 mb-1">
            Lägg till med e-post
          </label>
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <Input
                id={`${uid}-epost`}
                type="email"
                value={epost}
                onChange={(e) => setEpost(e.target.value)}
                placeholder="namn@el-kretsen.se"
                autoComplete="off"
                disabled={upptagen !== null}
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              disabled={!epost.trim() || upptagen !== null}
              loading={upptagen === 'ny'}
            >
              Lägg till
            </Button>
          </div>
          <p className="text-[12px] text-ink-500 mt-1.5">Personen behöver ett konto i ELvis Hub.</p>
        </form>
      )}
      {fel && (
        <div className="mt-3">
          <Besked ton="fel">{fel}</Besked>
        </div>
      )}
    </Sektion>
  );
}

/* ---------- Typ ---------- */

function Typ({ mote: m, agare, onBytt }: { mote: Mote; agare: boolean; onBytt: () => void }) {
  const uid = useId();
  const [val, setVal] = useState<MotesTyp>(m.typ);
  const [bekrafta, setBekrafta] = useState(false);
  const [byter, setByter] = useState(false);
  const [fel, setFel] = useState<string | null>(null);

  // Ett typbyte någon annanstans (eller en omhämtning) ska synas i väljaren.
  const [sparadTyp, setSparadTyp] = useState(m.typ);
  if (sparadTyp !== m.typ) {
    setSparadTyp(m.typ);
    setVal(m.typ);
  }

  async function byt() {
    setByter(true);
    setFel(null);
    try {
      await motenApi({ atgard: 'byt_typ', id: m.id, typ: val });
      setBekrafta(false);
      onBytt();
    } catch (e) {
      setFel(felText(e, 'Typbytet'));
    } finally {
      setByter(false);
    }
  }

  const raderasDatum = (() => {
    const d = new Date(m.skapad_at);
    d.setMonth(d.getMonth() + 12);
    return datumText(d.toISOString());
  })();

  return (
    <Sektion rubrik="Typ">
      {!agare ? (
        <>
          <p className="text-[13px] font-semibold text-ink-900">{TYP_ETIKETT[m.typ]}</p>
          <p className="text-[12.5px] text-ink-600 mt-0.5 leading-relaxed">{TYP_FORKLARING[m.typ]}</p>
        </>
      ) : (
        <>
          <label htmlFor={`${uid}-typ`} className="sr-only">
            Mötets typ
          </label>
          <div className="flex gap-2">
            <select
              id={`${uid}-typ`}
              value={val}
              onChange={(e) => setVal(e.target.value as MotesTyp)}
              className="flex-1 min-w-0 h-9 rounded-md border border-ink-200 bg-ink-50 text-ink-900 px-2.5 text-[13px] focus:border-brand-400 focus:bg-surface focus:ring-2 focus:ring-brand-100 focus:outline-none"
            >
              {(['intern_alla', 'intern_begransad', 'kund'] as MotesTyp[]).map((t) => (
                <option key={t} value={t}>
                  {TYP_ETIKETT[t]}
                </option>
              ))}
            </select>
            <Button variant="secondary" size="md" disabled={val === m.typ} onClick={() => setBekrafta(true)}>
              Byt
            </Button>
          </div>
          <p className="text-[12.5px] text-ink-600 mt-2 leading-relaxed">{TYP_FORKLARING[val]}</p>
          {m.raderas_at && (
            <p className="text-[12px] text-ink-500 mt-1.5">Mötet raderas helt {datumText(m.raderas_at)}.</p>
          )}
        </>
      )}

      <Modal
        open={bekrafta}
        onClose={() => !byter && setBekrafta(false)}
        title={`Byt till ${TYP_ETIKETT[val].toLowerCase()}`}
        size="md"
      >
        <div className="p-5 space-y-3">
          {val === 'kund' ? (
            <>
              <p className="text-[13.5px] text-ink-800 leading-relaxed">
                Kundmöten läggs aldrig i kunskapsbasen.
                {m.kb_tillagd_at ? ' Mötet tas bort ur kunskapsbasen direkt, och ELvis slutar svara utifrån det.' : ''}
              </p>
              <p className="text-[13.5px] text-ink-800 leading-relaxed">
                Mötet raderas helt 12 månader efter att det skapades, {raderasDatum}. Bara du och de du lagt till kan
                läsa det.
              </p>
            </>
          ) : (
            <p className="text-[13.5px] text-ink-800 leading-relaxed">
              {TYP_FORKLARING[val]}
              {m.typ === 'kund' ? ' Mötet raderas inte längre automatiskt efter 12 månader.' : ''}
            </p>
          )}
          {fel && <Besked ton="fel">{fel}</Besked>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setBekrafta(false)} disabled={byter}>
              Avbryt
            </Button>
            <Button onClick={() => void byt()} loading={byter}>
              Byt typ
            </Button>
          </div>
        </div>
      </Modal>
    </Sektion>
  );
}

/* ---------- Kunskapsbasen ---------- */

function Kunskapsbas({
  mote: m,
  kan,
  upptagen,
  agare,
  onLaggIn,
}: {
  mote: Mote;
  kan: boolean;
  upptagen: boolean;
  agare: boolean;
  onLaggIn: () => void;
}) {
  if (m.typ === 'kund') {
    return (
      <Sektion rubrik="Kunskapsbasen">
        <p className="text-[12.5px] text-ink-600 leading-relaxed">Kundmöten läggs aldrig i kunskapsbasen.</p>
      </Sektion>
    );
  }
  if (!m.kb_tillagd_at && !kan) {
    if (m.status === 'sammanfattad' || !agare) return null;
    return (
      <Sektion rubrik="Kunskapsbasen">
        <p className="text-[12.5px] text-ink-600 leading-relaxed">
          När mötet är sammanfattat kan det läggas i kunskapsbasen.
        </p>
      </Sektion>
    );
  }
  return (
    <Sektion rubrik="Kunskapsbasen">
      {m.kb_tillagd_at ? (
        <p className="text-[12.5px] text-ink-700 leading-relaxed">
          Ligger i kunskapsbasen sedan {datumTidText(m.kb_tillagd_at)}. ELvis kan svara på vad som bestämdes.
        </p>
      ) : (
        <p className="text-[12.5px] text-ink-600 leading-relaxed">
          Rubrik, datum, sammanfattning, beslut och åtgärder läggs in så att ELvis kan svara på dem. Utskriften läggs
          aldrig in.
        </p>
      )}
      {kan && (
        <Button
          variant={m.kb_tillagd_at ? 'secondary' : 'primary'}
          size="sm"
          className="mt-3"
          onClick={onLaggIn}
          loading={upptagen}
        >
          {m.kb_tillagd_at ? 'Uppdatera i kunskapsbasen' : 'Lägg i kunskapsbasen'}
        </Button>
      )}
    </Sektion>
  );
}
